interface RGB {
  r: number;
  g: number;
  b: number;
}

/**
 * Converts hex color string (e.g. "#ff9900") to an RGB object.
 */
function hexToRgb(hex: string): RGB {
  const cleanHex = hex.replace("#", "");
  const bigint = parseInt(cleanHex, 16);

  if (cleanHex.length === 3) {
    // Handle shorthand (#abc → #aabbcc)
    const r = parseInt(cleanHex[0] + cleanHex[0], 16);
    const g = parseInt(cleanHex[1] + cleanHex[1], 16);
    const b = parseInt(cleanHex[2] + cleanHex[2], 16);
    return { r, g, b };
  }

  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return { r, g, b };
}

/**
 * Converts an RGB object back to hex color string.
 */
function rgbToHex({ r, g, b }: RGB): string {
  return (
    "#" +
    [r, g, b]
      .map(x => {
        const hex = Math.round(x).toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
  );
}

/**
 * Linearly interpolate between two colors.
 */
function interpolateColor(color1: string, color2: string, t: number): string {
  const c1 = hexToRgb(color1);
  const c2 = hexToRgb(color2);

  const r = c1.r + (c2.r - c1.r) * t;
  const g = c1.g + (c2.g - c1.g) * t;
  const b = c1.b + (c2.b - c1.b) * t;

  return rgbToHex({ r, g, b });
}

/**
 * Returns a gradient-interpolated color based on a numeric value.
 *
 * @param min Lower bound of the value domain
 * @param max Upper bound of the value domain
 * @param value Input value to map
 * @param colors Gradient palette (array of hex strings)
 * @param reverse If true, invert the palette order
 */
export function colorByColorScheme(
  min: number,
  max: number,
  value: number,
  colors?: string[],
  reverse: boolean = false
): string {
  if (!Array.isArray(colors) || colors.length === 0) {
    throw new Error("colors array must contain at least one color");
  }
  if (colors.length === 1) return colors[0];

  if (reverse) colors = [...colors].reverse();

  // Clamp and normalize value
  if (min === max) return colors[0];
  let t = (value - min) / (max - min);
  t = Math.max(0, Math.min(1, t));

  // Map t into the correct color segment
  const segment = 1 / (colors.length - 1);
  const idx = Math.min(Math.floor(t / segment), colors.length - 2);
  const localT = (t - idx * segment) / segment;

  return interpolateColor(colors[idx], colors[idx + 1], localT);
}
